<template>
  <ActionMenu class="doc-library-menu" :position="position">
    <template #button>
      <slot></slot>
    </template>

    <Action
      v-if="doc.list === 'reading'"
      icon="ifinished"
      @click.prevent="moveTo('finished')"
    >
      Move to Finished
    </Action>

    <Action
      v-if="doc.list == 'finished'"
      icon="ireading"
      @click.prevent="moveTo('reading')"
    >
      Move to Currently Reading
    </Action>

    <Action
      v-if="!doc.list || doc.list == 'removed'"
      icon="ifinished"
      @click.prevent="moveTo('reading')"
    >
      Add to Currently Reading
    </Action>

    <Action
      v-if="doc.list && doc.list != 'removed'"
      icon="iexport"
      @click.prevent="moveTo('removed')"
    >
      Remove from My Library
    </Action>

    <Action
      v-if="isSingleDocPage && doc.amazon_link"
      icon="icart-buy"
      :href="doc.amazon_link"
    >
      Buy on Amazon
    </Action>
  </ActionMenu>
</template>

<script setup lang="ts">
import { DocsView, DocView } from '@/models/doc'
import { DocListItem } from '@/models/interfaces'

import ActionMenu from '@/app/components/ActionMenu.vue'
import Action from '@/app/components/ActionMenuItem.vue'

/**
 * This component is a wrapper on top of `ActionMenu` with
 * actions related to moving docs between the My Library
 * standard lists ("Reading Now", "Favorites" and "Finished").
 *
 * It can be used in a doc listing page by passing the `docsView`
 * prop, or in a single doc page, by passing a `DocView` instance to
 * the `doc` prop.
 *
 * @param doc - The doc upon which these actions will take effect.
 *   In a listing page, it can be a `DocListItem` (but must be paired
 *   with the `docsView` prop).
 *   In a single doc page, it can be a `DocView` instance.
 * @param docsView - A `DocsView` instance. This prop should be used when
 *   `doc` is not an instance of `DocsView`.
 * @param position - The aligment of the dropdown list relative to the button.
 */
const props = defineProps<{
  doc: DocListItem | DocView
  docsView?: DocsView
  position?: 'center' | 'right'
}>()

const isSingleDocPage = props.doc instanceof DocView
const isListPage = props.docsView && !isSingleDocPage

if (!isSingleDocPage && !isListPage) {
  throw new Error(
    'Incorrect usage of the component.' +
      'If `doc` is not an instance of `DocView`,' +
      'then `docsView` must not be undefined.',
  )
}

async function moveTo(list: string): Promise<void> {
  if (isSingleDocPage) {
    await props.doc.moveTo(list)
  } else if (isListPage) {
    await props.docsView.moveTo(props.doc, list)
  }
}
</script>

<style lang="scss" scoped>
.doc-library-menu {
  --button-color: #{$c-black-9};
  --button-background-hover: #{rgba($c-black-2, 0.05)};
}

.night-mode .doc-library-menu {
  --button-background-hover: #{rgba($c-dark-3, 0.5)};
}
</style>
